import React from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Stack,
  Link as MuiLink,
} from '@mui/material';
import { useEditUserSecurityMutation } from 'renderer/features/users-page/services/users.endpoints';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { useFormik } from 'formik';
import { z } from 'zod';
import { toValidator } from 'renderer/shared/schemas/common.schemas';
import { Link } from 'react-router-dom';

const SecuritySchema = z
  .object({
    currentPassword: z.string(),
    newPassword: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters long' }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type Security = z.infer<typeof SecuritySchema>;

interface SecurityTabProps {
  userId: string;
}

const SecurityTab: React.FC<SecurityTabProps> = ({ userId }) => {
  const [editUserSecurity, { isLoading, isSuccess, error }] =
    useEditUserSecurityMutation();

  const formik = useFormik<Security>({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate: toValidator(SecuritySchema),
    onSubmit: (values) => {
      editUserSecurity({ ...values, userId });
    },
  });

  return (
    <Box display="flex" justifyContent="left">
      <Paper elevation={0} sx={{ p: 2, width: '100%', maxWidth: 500 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: 5 }}>
          Security Settings{' '}
        </Typography>

        <Typography variant="body2" sx={{ mb: 3 }}>
          <MuiLink component={Link} to="/forgot-password">
            Forgot your password?
          </MuiLink>
        </Typography>

        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={3}>
            <TextField
              size="small"
              label="Current Password"
              type="password"
              name="currentPassword"
              id="currentPassword"
              disabled={isLoading}
              value={formik.values.currentPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.currentPassword &&
                !!formik.errors.currentPassword
              }
              helperText={
                formik.touched.currentPassword && formik.errors.currentPassword
              }
              fullWidth
            />

            <TextField
              size="small"
              label="New Password"
              type="password"
              name="newPassword"
              id="newPassword"
              disabled={isLoading}
              value={formik.values.newPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.newPassword && !!formik.errors.newPassword}
              helperText={
                formik.touched.newPassword && formik.errors.newPassword
              }
              fullWidth
            />

            <TextField
              size="small"
              label="Confirm New Password"
              type="password"
              name="confirmPassword"
              id="confirmPassword"
              disabled={isLoading}
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.confirmPassword &&
                !!formik.errors.confirmPassword
              }
              helperText={
                formik.touched.confirmPassword && formik.errors.confirmPassword
              }
              fullWidth
            />

            {error && <Alert severity="error">{formatQueryError(error)}</Alert>}

            {isSuccess && (
              <Alert severity="success">Password successfully changed!</Alert>
            )}

            <Box display="flex" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading || !formik.isValid}
              >
                Update Password
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default SecurityTab;
